import { type SerializedError } from '@databases/types';
import {
  type AvailableContentStates,
  type BlogpostReferenceOrContentId,
  type Content,
  type Excerpt,
  type PageReferenceOrContentId,
  type ResolvedBlogpostReference,
  type ResolvedPageReference,
} from '@databases/types/confluence';

import {
  blogpostToReference,
  getContentKey,
  isContent,
  pageToReference,
} from '../../confluence/utils';
import { type PayloadAction, createSlice } from '../../shims/redux-toolkit';
import { type AsyncOperation, AsyncOperationStatus } from '../async-operation';

export interface ContentState {
  contentRefs: Record<string, ResolvedPageReference | ResolvedBlogpostReference>;
  content: Record<string, Content>;
  coverPictureByContentId: Record<string, string | undefined>;
  excerptByContentId: Record<string, Excerpt | undefined>;

  contentRefStatus: Record<string, AsyncOperation<void>>;
  contentByIdStatus: Record<string, AsyncOperation<void>>;
  coverPictureStatus: Record<string, AsyncOperation<void>>;
  excerptStatus: Record<string, AsyncOperation<void>>;

  availableContentStates: Record<string, AvailableContentStates>;
  availableContentStatesStatus: Record<string, AsyncOperation<void>>;
}

const initialState: ContentState = {
  contentRefs: {},
  content: {},
  coverPictureByContentId: {},
  excerptByContentId: {},

  contentRefStatus: {},
  contentByIdStatus: {},
  coverPictureStatus: {},
  excerptStatus: {},

  availableContentStates: {},
  availableContentStatesStatus: {},
};

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    loadContentRefPending: (
      state,
      action: PayloadAction<PageReferenceOrContentId | BlogpostReferenceOrContentId>
    ) => {
      const contentRef = action.payload;

      if ('type' in contentRef) {
        state.contentRefStatus[getContentKey(contentRef)] = {
          status: AsyncOperationStatus.PENDING,
        };
      }

      if ('contentId' in contentRef && contentRef.contentId) {
        state.contentRefStatus[contentRef.contentId] = {
          status: AsyncOperationStatus.PENDING,
        };
      }
    },

    loadContentRefFinished: (
      state,
      action: PayloadAction<PageReferenceOrContentId | BlogpostReferenceOrContentId>
    ) => {
      const contentRef = action.payload;

      if ('type' in contentRef) {
        state.contentRefStatus[getContentKey(contentRef)] = {
          status: AsyncOperationStatus.SUCCEEDED,
        };
      }

      if ('contentId' in contentRef && contentRef.contentId) {
        state.contentRefStatus[contentRef.contentId] = {
          status: AsyncOperationStatus.SUCCEEDED,
        };
      }
    },

    loadContentRefError: (
      state,
      action: PayloadAction<
        (PageReferenceOrContentId | BlogpostReferenceOrContentId) & {
          error: SerializedError;
        }
      >
    ) => {
      const { error, ...contentRef } = action.payload;

      if ('type' in contentRef) {
        state.contentRefStatus[getContentKey(contentRef)] = {
          status: AsyncOperationStatus.ERROR,
          error,
        };
      }

      if ('contentId' in contentRef && contentRef.contentId) {
        state.contentRefStatus[contentRef.contentId] = {
          status: AsyncOperationStatus.ERROR,
          error,
        };
      }
    },

    loadContentByIdPending: (state, action: PayloadAction<{ contentId: string }>) => {
      const { contentId } = action.payload;

      state.contentByIdStatus[contentId] = { status: AsyncOperationStatus.PENDING };
    },

    loadContentByIdFinished: (state, action: PayloadAction<{ contentId: string }>) => {
      const { contentId } = action.payload;

      state.contentByIdStatus[contentId] = { status: AsyncOperationStatus.SUCCEEDED };
    },

    loadContentByIdError: (
      state,
      action: PayloadAction<{ contentId: string; error: SerializedError }>
    ) => {
      const { contentId, error } = action.payload;

      state.contentByIdStatus[contentId] = { status: AsyncOperationStatus.ERROR, error };
    },

    loadCoverPicturePending: (state, action: PayloadAction<{ contentId: string }>) => {
      const { contentId } = action.payload;

      state.coverPictureStatus[contentId] = { status: AsyncOperationStatus.PENDING };
    },

    loadCoverPictureFinished: (
      state,
      action: PayloadAction<{ contentId: string; coverPicture: string | undefined }>
    ) => {
      const { contentId, coverPicture } = action.payload;

      state.coverPictureStatus[contentId] = { status: AsyncOperationStatus.SUCCEEDED };
      state.coverPictureByContentId[contentId] = coverPicture;
    },

    loadCoverPictureError: (
      state,
      action: PayloadAction<{ contentId: string; error: SerializedError }>
    ) => {
      const { contentId, error } = action.payload;

      state.coverPictureStatus[contentId] = { status: AsyncOperationStatus.ERROR, error };
    },

    loadExcerptPending: (state, action: PayloadAction<{ contentId: string }>) => {
      const { contentId } = action.payload;

      state.excerptStatus[contentId] = { status: AsyncOperationStatus.PENDING };
    },

    loadExcerptFinished: (
      state,
      action: PayloadAction<{ contentId: string; excerpt: Excerpt }>
    ) => {
      const { contentId, excerpt } = action.payload;

      state.excerptStatus[contentId] = { status: AsyncOperationStatus.SUCCEEDED };
      state.excerptByContentId[contentId] = excerpt;
    },

    loadExcerptError: (
      state,
      action: PayloadAction<{ contentId: string; error: SerializedError }>
    ) => {
      const { contentId, error } = action.payload;

      state.excerptStatus[contentId] = { status: AsyncOperationStatus.ERROR, error };
    },

    addContentToList: (
      state,
      action: PayloadAction<{
        content: Array<Content | ResolvedPageReference | ResolvedBlogpostReference>;
      }>
    ) => {
      const { content } = action.payload;

      for (const obj of content) {
        if (
          !isContent(obj) ||
          obj.type === 'page' ||
          (obj.type === 'blogpost' &&
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            /* Skip unsaved blogpost drafts */ obj.history!.createdDate)
        ) {
          const contentRef = isContent(obj)
            ? obj.type === 'blogpost'
              ? blogpostToReference(obj)
              : pageToReference(obj)
            : obj;

          if ('type' in obj) {
            state.contentRefs[getContentKey(obj)] = contentRef;
          }
          state.contentRefs[contentRef.contentId] = contentRef;
        }

        if (isContent(obj)) {
          state.content[obj.id] = obj;
          state.contentByIdStatus[obj.id] = { status: AsyncOperationStatus.SUCCEEDED };
        }
      }
    },

    loadAvailableContentStatesPending: (
      state,
      action: PayloadAction<{ contentId: string }>
    ) => {
      const { contentId } = action.payload;

      state.availableContentStatesStatus[contentId] = {
        status: AsyncOperationStatus.PENDING,
      };
    },

    loadAvailableContentStatesFinished: (
      state,
      action: PayloadAction<{ contentId: string; states: AvailableContentStates }>
    ) => {
      const { contentId, states } = action.payload;

      state.availableContentStatesStatus[contentId] = {
        status: AsyncOperationStatus.SUCCEEDED,
      };
      state.availableContentStates[contentId] = states;
    },

    loadAvailableContentStatesError: (
      state,
      action: PayloadAction<{ contentId: string; error: SerializedError }>
    ) => {
      const { contentId, error } = action.payload;

      state.availableContentStatesStatus[contentId] = {
        status: AsyncOperationStatus.ERROR,
        error,
      };
    },

    resetAvailableContentState: (state) => {
      state.availableContentStates = {};
      state.availableContentStatesStatus = {};
    },
  },
});

export default contentSlice;
